import { useState, useEffect } from 'react';
import { ClientEvent, SyncState } from '../constants/matrixEvents';
import logger from '../utils/logger';

/**
 * Shared hook that tracks the current user's ignored users list.
 *
 * Listens for `m.ignored_user_list` account data changes and initializes
 * from the client when sync is ready. Used by both useRoomList and
 * useChannels to filter conversation previews.
 *
 * @param {MatrixClient} client - The Matrix client instance
 * @returns {string[]} Array of ignored user IDs
 */
export const useIgnoredUsers = (client) => {
  const [ignoredUsers, setIgnoredUsers] = useState([]);

  useEffect(() => {
    if (!client) {
      setIgnoredUsers([]);
      return;
    }

    const updateIgnoredUsers = () => {
      setIgnoredUsers(prev => {
        const next = (typeof client.getIgnoredUsers === 'function' ? client.getIgnoredUsers() : null) || [];
        // Use Set-based comparison to avoid order sensitivity
        if (prev.length === next.length && new Set(prev).size === new Set([...prev, ...next]).size) {
          return prev;
        }
        return next;
      });
    };

    const handleAccountData = (event) => {
      if (event.getType() === 'm.ignored_user_list') {
        logger.debug('[useIgnoredUsers] m.ignored_user_list changed, updating');
        updateIgnoredUsers();
      }
    };

    // Initialize if sync is already ready
    const syncState = client.getSyncState();
    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) {
      updateIgnoredUsers();
    }

    client.on(ClientEvent.AccountData, handleAccountData);

    return () => {
      client.off(ClientEvent.AccountData, handleAccountData);
    };
  }, [client]);

  return ignoredUsers;
};

export default useIgnoredUsers;
